const STORAGE_PREFIX = "knotfin:lastPosition:";
const STORAGE_TTL_MS = 24 * 60 * 60 * 1000;

interface StoredPosition {
  positionMS: number;
  savedAt: number;
}

function storageKey(itemId: string): string {
  return `${STORAGE_PREFIX}${itemId}`;
}

function getStorage(): Storage | null {
  try {
    if (typeof window === "undefined") return null;
    return window.localStorage;
  } catch {
    return null;
  }
}

export function writeLocalPlaybackPosition(itemId: string | null | undefined, positionMS: number): void {
  if (!itemId) return;
  if (!Number.isFinite(positionMS) || positionMS <= 0) return;
  const storage = getStorage();
  if (!storage) return;
  try {
    const payload: StoredPosition = { positionMS: Math.floor(positionMS), savedAt: Date.now() };
    storage.setItem(storageKey(itemId), JSON.stringify(payload));
  } catch {
    // Quota exceeded or storage disabled — silently ignore
  }
}

export function readLocalPlaybackPosition(itemId: string | null | undefined): StoredPosition | null {
  if (!itemId) return null;
  const storage = getStorage();
  if (!storage) return null;
  try {
    const raw = storage.getItem(storageKey(itemId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredPosition>;
    if (typeof parsed?.positionMS !== "number" || typeof parsed?.savedAt !== "number") return null;
    if (parsed.positionMS <= 0) return null;
    if (Date.now() - parsed.savedAt > STORAGE_TTL_MS) {
      storage.removeItem(storageKey(itemId));
      return null;
    }
    return { positionMS: parsed.positionMS, savedAt: parsed.savedAt };
  } catch {
    return null;
  }
}

export function clearLocalPlaybackPosition(itemId: string | null | undefined): void {
  if (!itemId) return;
  const storage = getStorage();
  if (!storage) return;
  try {
    storage.removeItem(storageKey(itemId));
  } catch {
    // ignore
  }
}

/**
 * Decide the best resume position when the same item has both a server
 * persisted position (with its own update timestamp) and a locally
 * mirrored position. Prefer whichever is newer; tie-break on higher
 * positionMS so we never resume earlier than the user has actually been.
 */
export function chooseResumePositionMS(
  serverPositionMS: number,
  serverUpdatedAt: string | null | undefined,
  itemId: string | null | undefined,
): number {
  const local = readLocalPlaybackPosition(itemId);
  if (!local) return serverPositionMS;
  const serverTime = serverUpdatedAt ? Date.parse(serverUpdatedAt) : 0;
  if (Number.isFinite(serverTime) && serverTime > 0 && serverTime >= local.savedAt) {
    return Math.max(serverPositionMS, local.positionMS);
  }
  return Math.max(serverPositionMS, local.positionMS);
}
